/**
 * Gemini AI Service
 * 
 * Service layer for interacting with Google's Gemini AI via Firebase Cloud Functions.
 * Handles chat sessions, message sending, and streaming responses.
 */

// Cloud Function URL - will be set after deployment
// Cloud Function URL - will be set after deployment
const CLOUD_FUNCTION_URL = import.meta.env.VITE_GEMINI_CLOUD_FUNCTION_URL ||
    'https://geminiproxy-p3aid3mwoq-uc.a.run.app';

// Helper for proxy download
const PROXY_DOWNLOAD_URL = import.meta.env.VITE_PROXY_DOWNLOAD_URL ||
    'https://proxyfiledownload-p3aid3mwoq-uc.a.run.app';

import { auth } from "../config/firebase";

/**
 * Send a message and get a response via Cloud Function
 * @param {string} message - User message
 * @param {Array} history - Chat history (optional)
 * @param {Array} attachments - Optional attachments [{ mimeType: string, data: base64String }]
 * @param {string} modelName - Model name (optional)
 * @returns {Promise<string>} AI response
 */
export const sendMessage = async (message, history = [], attachments = [], modelName = 'gemini-2.0-flash') => {
    try {
        const user = auth.currentUser;
        if (!user) {
            throw new Error("User not authenticated");
        }
        const idToken = await user.getIdToken();

        const response = await fetch(CLOUD_FUNCTION_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
                message,
                history,
                attachments,
                modelName
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Cloud Function error: ${errorText}`);
        }

        const data = await response.json();
        return data.text;
    } catch (error) {
        console.error('Error sending message to Gemini Cloud Function:', error);
        throw error;
    }
};

/**
 * Send a message with streaming response
 * Note: Streaming is not yet implemented in the Cloud Function
 * This falls back to regular sendMessage for now
 * @param {string} message - User message
 * @param {Array} history - Chat history (optional)
 * @param {Function} onChunk - Callback for each chunk of response
 * @param {Array} attachments - Optional attachments
 * @returns {Promise<string>} Complete AI response
 */
export const sendMessageStream = async (message, history = [], onChunk, attachments = []) => {
    try {
        // For now, we'll use the regular sendMessage and simulate streaming
        // In the future, we can implement Server-Sent Events (SSE) in the Cloud Function
        const response = await sendMessage(message, history, attachments);

        // Simulate streaming by sending the response in chunks
        if (onChunk) {
            const words = response.split(' ');
            let accumulated = '';

            for (const word of words) {
                accumulated += (accumulated ? ' ' : '') + word;
                onChunk(word + ' ');
                // Small delay to simulate streaming
                await new Promise(resolve => setTimeout(resolve, 10));
            }
        }

        return response;
    } catch (error) {
        console.error('Error streaming message from Gemini Cloud Function:', error);
        throw error;
    }
};

/**
 * Get Gemini model instance (deprecated - kept for compatibility)
 * @param {string} modelName - Model name
 * @returns {Object} Mock model object
 */
export const getModel = (modelName = 'gemini-2.0-flash') => {
    console.warn('getModel is deprecated. Use sendMessage directly.');
    return {
        modelName,
        generateContent: async (parts) => {
            const message = parts.find(p => p.text)?.text || '';
            const attachments = parts.filter(p => p.inlineData);
            const text = await sendMessage(message, [], attachments, modelName);
            return { response: { text: () => text } };
        }
    };
};

/**
 * Check if Gemini API is available
 * @returns {boolean} True if Cloud Function URL is configured
 */
export const isGeminiAvailable = () => {
    return !!CLOUD_FUNCTION_URL;
};

/**
 * Download file via Cloud Function Proxy (bypasses CORS)
 * @param {string} url - The URL to download
 * @returns {Promise<{base64Data: string, mimeType: string}>}
 */
export const downloadFileViaProxy = async (url) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error("User not authenticated");
        const idToken = await user.getIdToken();

        // Use the same base URL pattern if PROXY_DOWNLOAD_URL is arguably same host
        // But for now assuming separate function deployment
        const response = await fetch(PROXY_DOWNLOAD_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ url })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Proxy download failed: ${errorText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error downloading file via proxy:', error);
        throw error;
    }
};
